import { Injectable } from '@angular/core';
import { prescriptionVar } from 'e2e/src/shared/constants';
import { EventLoggerService } from '@services/event-logger-service';
import { ConnectionService } from '../connection-service';
import { DoctorProfile } from '@cureskin/api-client-rn';

@Injectable({
  providedIn: 'root'
})
export class UserAllocatedDoctorService {
  private readonly DEFAULT_DOCTOR_NAME = 'Dr. Charu';
  private readonly DEFAULT_DOCTOR_IMAGE = 'https://storage.googleapis.com/heallo-other-user-files/doctor_api_charu_07302024_1722317224002.jpg';
  readonly DEFAULT_DOCTOR_IMAGE_THUMBNAIL = 'https://storage.googleapis.com/heallo-other-user-files/doctor_api_charu_07302024_1722317222409.png';

  constructor(
    private readonly conn: ConnectionService,
    private readonly eventLogger: EventLoggerService,
  ) {}

  /**
   * Resolve the acting user's allocated doctor as a projected (non-PII) plain object via REST.
   * Always uses the acting user — a customer can only ever fetch their own allocated doctor.
   * @param withDefault When false (default), returns `null` if there is no allocated doctor —
   *   callers that hide the doctor section guard on null. When true, never returns null:
   *   `DoctorDisplayName`/`doctorDisplayImage` are backfilled with the default doctor for
   *   callers that always render a doctor.
   */
  async getAllocatedDoctor(withDefault = false): Promise<DoctorProfile | null> {
    const id = this.conn.getActingUser()?.get('allocatedDoctor')?.id;
    let doctor: DoctorProfile | null = null;
    if (id) {
      try {
        doctor = await this.conn.findDoctorById(id);
      } catch (error) {
        this.eventLogger.logError(error, false, 'UserAllocatedDoctorService.getAllocatedDoctor');
      }
    }
    if (!withDefault) return doctor;
    return {
      ...doctor,
      DoctorDisplayName: doctor?.DoctorDisplayName || this.DEFAULT_DOCTOR_NAME,
      doctorDisplayImage: doctor?.doctorDisplayImage || this.DEFAULT_DOCTOR_IMAGE,
    } as DoctorProfile;
  }

  /** Fetch a doctor's projected (non-PII) profile by objectId via REST. */
  getDoctorById(id: string): Promise<DoctorProfile> {
    return this.conn.findDoctorById(id);
  }

  getQualifyText(doctor: DoctorProfile): string {
    return doctor?.forDoctor ? 'Physician and skin care expert' : doctor?.doctorQualification;
  }

  /**
   * Resolve the verifying doctor's display name + signature for a prescription (by order id).
   * Fetches the latest DR_VERIFIED consultation, then the doctor via REST; falls back to the
   * prescription defaults when there is no verified doctor or on error (never throws).
   */
  async getPrescriptionDoctorInfo(orderId: string): Promise<{ name: string; signature: string }> {
    try {
      const consultation = await this.conn.findLastVerifiedConsultation(orderId);
      const doctorId = consultation?.get('createdBy')?.id;
      const doctor = doctorId ? await this.getDoctorById(doctorId) : null;
      return {
        name: doctor?.DoctorDisplayName || prescriptionVar.name,
        signature: doctor?.doctorSignature || prescriptionVar.signature,
      };
    } catch (error) {
      this.eventLogger.logError(error, false, 'UserAllocatedDoctorService.getPrescriptionDoctorInfo');
      return { name: prescriptionVar.name, signature: prescriptionVar.signature };
    }
  }
}
